import { Injectable }     from '@angular/core';
import { Storage }        from '@ionic/storage';

import { ApiService }     from './api.service';
import { Book, BookList } from '../models/book';
import helper             from '../helper';

const MAX_PRELOAD_COUNT = 100,
  MAX_RUNNING_COUNT = 4;

@Injectable()
export class BookService {
  private books = {};
  private loadChapterPromises = {};
  private preloadQueue= [];
  private preloadCount: number = 0;

  public constructor(private storage: Storage, private apiService: ApiService) {
    // this.loadFromCache();
    this.init();
    // this.storage.clear();
  }

  public async init() {
  }
  public async getBooks(options?): Promise<BookList> {
    let page = options.page || 1;

    options.bookmarked = options.bookmarked ? 1 : 0;
    let data = await this.apiService.callOnce('books', 'GET', options),
      books = this.processBooks(data.books);

    return new BookList({
      books: books,
      page: page,
      total: data.total,
      pages: data.total_pages
    });
  }
  public async getBook(bookAlias, options?, needChapters = true): Promise<Book> {
    let storedBook = await this.loadBook(bookAlias);
    if (storedBook && (!needChapters || storedBook.chapters) && !(options && (options.crawl || options.reload))) return storedBook;

    let data = await this.apiService.call(`books/${bookAlias}`, 'GET', options);

    return this.processBook(data);
  }
  public getRecent(book: Book, options?): Promise<any> {
    return this.apiService.call(`books/${book.id}/recent`, 'GET', options);
  }
  public async getChapters(bookAlias: string, chapterIds: number[], options?): Promise<any> {
    let book: Book = await this.loadBook(bookAlias);
    options = options || {};
    if (!Array.isArray(chapterIds) || chapterIds.length <= 0) return [];
    options.ids = chapterIds.join(',');
    let chapters = await this.apiService.call(`books/${book.id}/chapters`, 'GET', options);

    console.log(chapters);
    for (let chapterData of chapters) {
      let chapterIndex = helper.findIndex(book.chapters, chapterData.id, 'id');
      let chapter = book.chapters[chapterIndex];
      this.processChapter(book, chapter, chapterData);
    }

    return chapters;
  }
  public async getChapter(bookAlias, chapterAlias, options?): Promise<any> {
    let book: Book, chapter, storeIndex, chapterIndex, chapterData, promise;
    book = await this.getBook(bookAlias);
    if (!book) throw {message: 'No book'};

    storeIndex = `${bookAlias}-${chapterAlias}`;
    if (this.loadChapterPromises[storeIndex]) return this.loadChapterPromises[storeIndex];

    chapterIndex = helper.findIndex(book.chapters, chapterAlias, 'alias');
    chapter = book.chapters[chapterIndex];

    console.log(chapter, chapter.content ? chapter.content.length : 0);

    if (!chapter) throw {message: 'Chapter not found'};

    if (!options || !options.crawl) {
      if (chapter.content && chapter.content.length > 0) return chapter;
      let storedChapter = await this.loadChapter(bookAlias, chapterAlias);
      if (storedChapter && storedChapter.content && storedChapter.content.length > 0) return storedChapter;
    }

    try {
    // console.log('Start loading chapter ' + chapter.alias);
    promise = this.loadChapterPromises[storeIndex] = this.apiService.call(`books/${book.id}/chapters/${chapter.id}`, 'GET', options);
    chapterData = await promise;
    delete this.loadChapterPromises[storeIndex];

    return this.processChapter(book, chapter, chapterData);

    } catch (error) {
      delete this.loadChapterPromises[storeIndex];

      throw error;
    }
  }

  public async toggleBookmark(book: Book): Promise<void> {
    await this.bookmark(book.id, !book.bookmarked);
    book.bookmarked = !book.bookmarked;
  }

  public bookmark(bookId: number, bookmarkStatus: boolean): Promise<any> {
    return this.apiService.call(`books/${bookId}/bookmark`, 'POST', {bookmark: bookmarkStatus});
  }
  public async read(book, chapter): Promise<any> {
    try {
      await this.apiService.callOnce(`books/${book.id}/chapters/${chapter.id}/read`, 'POST');
      let chapterIndex = helper.findIndex(book.chapters, chapter.id, 'id');
      if (chapterIndex >= 0) {
        book.chapters[chapterIndex].read = true;
        if (book.bookmarked) this.storeBook(book);
      }
      chapter.read = true;
      // this.storeChapter(book, chapter);
    } catch (error) {
      throw error;
    }
  }

  public async preloadChapters(bookAlias, fromIndex, loadCount = 0): Promise<any> {
    let book: Book = await this.loadBook(bookAlias);
    if (!book) return;
    
    if (!loadCount) loadCount = MAX_PRELOAD_COUNT;

    let count = 0;
    let ids = [];
    for (let i = 0; i < book.chapters.length; i++) {
      if (MAX_PRELOAD_COUNT && count >= loadCount) break;
      count++;
      let index = (fromIndex + i - 1 < book.chapters.length) ? fromIndex + i - 1 : fromIndex + i - 1 - book.chapters.length,
        chapter = book.chapters[index];

      if (!chapter || (chapter.content && chapter.content.length > 0)) continue;
      // this.preloadQueue.push({bookAlias: bookAlias, chapterAlias: chapter.alias});
      ids.push(chapter.id);
    }

    return this.getChapters(book.alias, ids);
  }
  public stopPreloadChapters() {
    this.preloadQueue = [];
  }
  private doPreloadChapters(): Promise<any> {
    return new Promise((resolve, reject) => {
      while (this.preloadCount < MAX_RUNNING_COUNT) {
        let preloadItem = this.preloadQueue.shift();
        if (!preloadItem) return;
        this.getChapter(preloadItem.bookAlias, preloadItem.chapterAlias)
          .then(
            () => this.preloadCount-- && this.doPreloadChapters() && this.preloadCount == 0 && resolve(),
            () => this.preloadCount-- && this.doPreloadChapters() && this.preloadCount == 0 && reject()
          );

        this.preloadCount++;
      }
    });
  }

  private processBooks(books) {
    return books.map(book => this.processBook(book, false));
  }

  private processBook(book, saveToStorage = true) {
    // console.log(book);
    if (book.bookmark && book.bookmark.status) {
      book.bookmarked = true;
    }

    if (saveToStorage) {
      if (book.bookmarked) this.storeBook(book);
      this.books[book.alias] = book;
    }

    if (!book.chapters || !Array.isArray(book.chapters)) return book;

    return book;
  }
  private processChapter(book, chapter, _chapter) {
    chapter.content = _chapter.content;
    chapter.loaded = 1;

    if (book.bookmarked) this.storeChapter(book, _chapter);

    return _chapter;
  }
  private storeBook(book: Book): Promise<void> {
    console.log(`Store ${book.alias}`);
    return this.storage.set(book.alias, book);
  }
  private async loadBook(bookAlias: string): Promise<Book> {
    if (this.books[bookAlias]) return this.books[bookAlias];
    let storedBook = await this.storage.get(bookAlias);
    if (!storedBook) return null;

    this.books[bookAlias] = storedBook;
    return storedBook;
  }
  private storeChapter(book: Book, chapter): Promise<void> {
    return this.storage.set(`${book.alias}-${chapter.alias}`, chapter);
  }
  private loadChapter(bookAlias: string, chapterAlias: string): Promise<any> {
    return this.storage.get(`${bookAlias}-${chapterAlias}`);
  }
}



// WEBPACK FOOTER //
// ./src/services/book.service.ts